package com.schoolconnect.app.feature.schools

import androidx.compose.runtime.Composable
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.designsystem.AppConfirmationDialog
import com.schoolconnect.app.designsystem.AppDanger
import com.schoolconnect.app.designsystem.AppPageHeader
import com.schoolconnect.app.designsystem.AppPageSubtitle
import com.schoolconnect.app.designsystem.AppConfirmationDialogTone
import com.schoolconnect.app.designsystem.AppLabeledDropdownField
import com.schoolconnect.app.designsystem.AppOutlinedTextField
import com.schoolconnect.app.designsystem.AppSuccess
import com.schoolconnect.app.domain.SchoolKeys
import com.schoolconnect.app.designsystem.list.AdminListFilterBanner
import com.schoolconnect.app.designsystem.list.AdminListFooter
import com.schoolconnect.app.designsystem.list.AdminListMessageAndFilters
import com.schoolconnect.app.designsystem.list.AdminListPageScaffold
import com.schoolconnect.app.designsystem.list.AdminListPrimaryActionButton
import com.schoolconnect.app.designsystem.list.AdminListHeaderColumn
import com.schoolconnect.app.designsystem.list.AdminListTableShimmer
import com.schoolconnect.app.designsystem.list.AdminListTableCard
import com.schoolconnect.app.designsystem.list.AdminListTableBody
import com.schoolconnect.app.designsystem.list.AdminListToolbar
import com.schoolconnect.app.designsystem.list.AdminListToolbarButton
import com.schoolconnect.app.feature.common.ShellTopRightActions
import com.schoolconnect.app.feature.common.footerText
import com.schoolconnect.app.feature.common.shellAvatarInitials
import com.schoolconnect.app.feature.common.shellRoleLabel
import com.schoolconnect.app.feature.common.shellRoleSubtitle
import com.schoolconnect.app.feature.schools.edit.EditSchoolFeatureRoute
import com.schoolconnect.app.feature.schools.edit.schoolRowToEditSchoolFormValues
import com.schoolconnect.app.feature.schools.details.SchoolDetailsFeatureRoute
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.info_circle
import ischoolconnect.sharedui.generated.resources.arrow_up
import ischoolconnect.sharedui.generated.resources.document
import ischoolconnect.sharedui.generated.resources.filter
import ischoolconnect.sharedui.generated.resources.refresh_cw
import ischoolconnect.sharedui.generated.resources.plus
import ischoolconnect.sharedui.generated.resources.search
import ischoolconnect.sharedui.generated.resources.trash
import org.jetbrains.compose.resources.painterResource

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SchoolsScreen(
    state: SchoolsUiState,
    onEvent: (SchoolsUiEvent) -> Unit,
    onBack: () -> Unit,
    shellRoleCode: String = "",
    shellSchoolLabel: String = "iConnect.School",
    onChangeRole: () -> Unit = onBack,
    onLogout: () -> Unit = {}
) {
    val page = filteredAndPagedSchools(state)
    val stats = schoolStats(state.schools)
    val pageSizeText = state.paging.footerText(page.items.size, "schools")
    val isSuperAdmin = shellRoleCode.trim() == SchoolKeys.V_ROLE_APP_SUPER_ADMIN
    var showFilterSheet by remember { mutableStateOf(false) }
    var pendingSchoolAction by remember { mutableStateOf<SchoolPendingAction?>(null) }
    var filterDraft by remember { mutableStateOf(state.filters) }
    var rowsPerPageExpanded by remember { mutableStateOf(false) }
    var accountMenuExpanded by remember { mutableStateOf(false) }
    val roleLabel = shellRoleLabel(shellRoleCode)
    val roleSubtitle = shellRoleSubtitle(shellRoleCode)
    val initials = shellAvatarInitials(roleLabel)
    val selectedEditSchool = state.selectedSchool.takeIf { state.screenMode == SchoolsScreenMode.EDIT }
    val showDetails = state.selectedSchool.takeIf { state.screenMode == SchoolsScreenMode.DETAILS }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(SchoolsBackground)
    ) {
            val activeSchools = if (isSuperAdmin) {
                state.schoolsAnalytics?.metrics?.activeSchools ?: stats.active
            } else {
                stats.active
            }
            val inactiveSchools = if (isSuperAdmin) {
                state.schoolsAnalytics?.metrics?.suspendedOrInactiveSchools ?: stats.inactive
            } else {
                stats.inactive
            }
            val schoolsSubtitle = remember(activeSchools, inactiveSchools) {
                buildAnnotatedString {
                    append("Manage all schools and their status. ")
                    withStyle(SpanStyle(color = AppSuccess, fontWeight = FontWeight.Bold)) {
                        append("$activeSchools Active")
                    }
                    append(" and ")
                    withStyle(SpanStyle(color = AppDanger, fontWeight = FontWeight.Bold)) {
                        append("$inactiveSchools Inactive")
                    }
                    append(" schools.")
                }
            }

            AdminListPageScaffold(
                title = "Schools",
                subtitle = "Manage all schools and their status",
                subtitleAnnotated = schoolsSubtitle,
                breadcrumb = listOf("School Setup", "Schools"),
                showSummaryCards = false,
                headerActions = {
                    ShellTopRightActions(
                        roleLabel = roleLabel,
                        roleSubtitle = roleSubtitle,
                        initials = initials,
                        expanded = accountMenuExpanded,
                        onExpandedChange = { accountMenuExpanded = it },
                        onChangeRole = onChangeRole,
                        onLogout = onLogout
                    )
                },
                backgroundColor = SchoolsBackground,
                titleColor = AppPageHeader,
                subtitleColor = AppPageSubtitle,
                summaryCards = {},
                banner = {
                    val filterLabels = schoolFilterLabels(state.searchQuery, state.filters)
                    val bannerMessage = state.errorMessage.ifBlank { state.infoMessage }
                    if (bannerMessage.isBlank() && filterLabels.isEmpty()) {
                        // The chip row lives on the toolbar. It only updates while this
                        // banner is composed, so an empty list has to clear it too.
                        AdminListFilterBanner(
                            chips = emptyList(),
                            onClearAll = {}
                        )
                    } else {
                        AdminListMessageAndFilters(
                            message = state.errorMessage.ifBlank { state.infoMessage },
                            onDismissMessage = { onEvent(SchoolsUiEvent.ClearMessages) },
                            chips = filterLabels,
                            onClearAll = {
                                    onEvent(SchoolsUiEvent.FilterChanged(SchoolFilterCriteria()))
                                    onEvent(SchoolsUiEvent.SearchChanged(""))
                            },
                            onRemoveChip = { chip ->
                                    if (chip.startsWith("Search:")) {
                                        onEvent(SchoolsUiEvent.SearchChanged(""))
                                    } else {
                                        val next = when {
                                            chip.startsWith("Status:") -> state.filters.copy(status = SchoolStatusFilter.ALL)
                                            chip.startsWith("Board:") -> state.filters.copy(board = "")
                                            chip.startsWith("Type:") -> state.filters.copy(schoolType = "")
                                            chip.startsWith("Medium:") -> state.filters.copy(medium = "")
                                            chip.startsWith("Min students:") -> state.filters.copy(minStudents = "")
                                            chip.startsWith("Max students:") -> state.filters.copy(maxStudents = "")
                                            chip.startsWith("Created from:") -> state.filters.copy(createdFrom = "")
                                            chip.startsWith("Created to:") -> state.filters.copy(createdTo = "")
                                            chip.startsWith("Updated from:") -> state.filters.copy(updatedFrom = "")
                                            chip.startsWith("Updated to:") -> state.filters.copy(updatedTo = "")
                                            else -> state.filters
                                        }
                                        onEvent(SchoolsUiEvent.FilterChanged(next))
                                    }
                            },
                            onEditFilters = {
                                    filterDraft = state.filters.copy(location = "")
                                    showFilterSheet = true
                            },
                            messageBackgroundColor = SchoolsBlueSoft,
                            messageBorderColor = SchoolsBorder,
                            messageContentColor = SchoolsTextPrimary,
                            messageAccentColor = SchoolsBlue,
                            filterBackgroundColor = Color(0xFFF2F7FF),
                            filterBorderColor = SchoolsBorder,
                            filterContentColor = SchoolsTextPrimary,
                            filterAccentColor = SchoolsBlue,
                            messageLeadingIcon = painterResource(Res.drawable.info_circle)
                        )
                    }
                },
                toolbar = {
                    AdminListToolbar(
                        searchQuery = state.searchQuery,
                        onSearchQueryChanged = { onEvent(SchoolsUiEvent.SearchChanged(it)) },
                        searchPlaceholder = "Search by school name, code or city...",
                        searchWidth = 370.dp,
                        surfaceColor = SchoolsSurface,
                        borderColor = SchoolsBorder,
                        searchIconTint = SchoolsTextSecondary,
                        searchTextColor = SchoolsTextPrimary,
                        searchCursorColor = SchoolsBlue,
                        inlineContent = {
                            AppLabeledDropdownField(
                                label = "",
                                value = state.filters.status.displayLabel().let { if (it == "All") "All Statuses" else it },
                                options = listOf("Active", "Inactive"),
                                onSelected = { selected ->
                                    onEvent(
                                        SchoolsUiEvent.FilterChanged(
                                            state.filters.copy(
                                                status = selected.toSchoolStatusFilter(),
                                                location = ""
                                            )
                                        )
                                    )
                                },
                                modifier = Modifier.width(160.dp),
                                allOptionLabel = "All Statuses"
                            )
                        }
                    ) {
                        AdminListToolbarButton(
                            label = "Reset",
                            icon = painterResource(Res.drawable.refresh_cw),
                            onClick = {
                                filterDraft = SchoolFilterCriteria()
                                onEvent(SchoolsUiEvent.FilterChanged(SchoolFilterCriteria()))
                                onEvent(SchoolsUiEvent.SearchChanged(""))
                            },
                            width = 112.dp,
                            borderColor = SchoolsBlue,
                            contentColor = SchoolsBlue
                        )
                        AdminListToolbarButton(
                            label = "More Filters",
                            icon = painterResource(Res.drawable.filter),
                            onClick = {
                                filterDraft = state.filters.copy(location = "")
                                showFilterSheet = true
                            },
                            width = 148.dp,
                            borderColor = SchoolsBlue,
                            contentColor = SchoolsBlue
                        )
                    }
                },
                table = { tableModifier ->
                    AdminListTableCard(
                        modifier = tableModifier,
                        minContentWidth = 1100.dp,
                        surfaceColor = SchoolsSurface,
                        borderColor = SchoolsBorder,
                        header = {
                            SchoolHeaderRow(
                                sortField = state.sortField,
                                sortAscending = state.isSortAscending,
                                onSortChanged = { onEvent(SchoolsUiEvent.SortChanged(it)) }
                            )
                        },
                        body = {
                            if (state.isLoading) {
                                AdminListTableShimmer(
                                    columns = schoolTableShimmerColumns(),
                                    rowCount = 6,
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalPadding = 24.dp
                                )
                            } else if (page.items.isEmpty()) {
                                val hasListCriteria = state.searchQuery.isNotBlank() ||
                                    state.filters.status != SchoolStatusFilter.ALL ||
                                    state.filters.hasAdvancedFilters()
                                Box(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .weight(1f),
                                    contentAlignment = Alignment.Center
                                ) {
                                    Column(horizontalAlignment = Alignment.CenterHorizontally) {
                                        Icon(
                                            painter = painterResource(
                                                if (hasListCriteria) Res.drawable.search else Res.drawable.document
                                            ),
                                            contentDescription = null,
                                            tint = SchoolsTextSecondary,
                                            modifier = Modifier.size(24.dp)
                                        )
                                        Spacer(modifier = Modifier.height(8.dp))
                                        Text(
                                            text = if (hasListCriteria) "No results found" else "No records available",
                                            color = SchoolsTextPrimary,
                                            fontSize = 16.sp,
                                            fontWeight = FontWeight.SemiBold
                                        )
                                        Spacer(modifier = Modifier.height(6.dp))
                                        Text(
                                            text = if (hasListCriteria) {
                                                "Try using a different search or filter criteria."
                                            } else {
                                                "There are no school records to display."
                                            },
                                            color = SchoolsTextSecondary,
                                            fontSize = 14.sp
                                        )
                                    }
                                }
                            } else {
                                AdminListTableBody(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .weight(1f),
                                    items = page.items,
                                    key = { it.id }
                                ) { school ->
                                    SchoolRowCard(
                                        school = school,
                                        onViewDetails = { onEvent(SchoolsUiEvent.ViewSchoolClicked(school)) },
                                        onEdit = {
                                            showFilterSheet = false
                                            onEvent(SchoolsUiEvent.EditSchoolClicked(school))
                                        },
                                        onToggleStatus = {
                                            pendingSchoolAction = SchoolPendingAction(
                                                school = school,
                                                action = SchoolPendingActionType.ToggleStatus
                                            )
                                        },
                                        onDelete = {
                                            pendingSchoolAction = SchoolPendingAction(
                                                school = school,
                                                action = SchoolPendingActionType.Delete
                                            )
                                        }
                                    )
                                    }
                            }
                        },
                        footer = {
                            AdminListFooter(
                                pageText = pageSizeText,
                                pageIndex = state.paging.pageIndex.coerceAtMost(page.pageCount - 1),
                                pageCount = page.pageCount,
                                onPageChanged = { onEvent(SchoolsUiEvent.PageChanged(it)) },
                                pageSize = state.paging.pageSize,
                                rowsPerPageExpanded = rowsPerPageExpanded,
                                onRowsPerPageToggle = { rowsPerPageExpanded = !rowsPerPageExpanded },
                                onRowsPerPageDismiss = { rowsPerPageExpanded = false },
                                onPageSizeChanged = { onEvent(SchoolsUiEvent.PageSizeChanged(it)) },
                                footerColor = SchoolsTextSecondary,
                                borderColor = SchoolsBorder,
                                surfaceColor = SchoolsSurface,
                                primaryColor = SchoolsBlue,
                                navyColor = SchoolsTextPrimary,
                                mutedColor = SchoolsTextSecondary
                            )
                        }
                    )
                },
                bottomAction = {
                    AdminListPrimaryActionButton(
                        label = "Add School",
                        icon = painterResource(Res.drawable.plus),
                        onClick = {
                            onEvent(SchoolsUiEvent.AddSchoolClicked)
                        },
                        backgroundColor = SchoolsBlue
                    )
                }
            )

            if (state.screenMode == SchoolsScreenMode.ADD) {
                AddSchoolScreen(
                    onBack = { onEvent(SchoolsUiEvent.CloseActiveScreen) },
                    isSaving = state.isSavingSchool,
                    onSaveSchool = { draft ->
                        onEvent(SchoolsUiEvent.SaveSchool(draft))
                    }
                )
            } else if (state.screenMode == SchoolsScreenMode.EDIT) {
                EditSchoolFeatureRoute(
                    school = selectedEditSchool,
                    initialValues = schoolRowToEditSchoolFormValues(selectedEditSchool),
                    onBack = {
                        onEvent(SchoolsUiEvent.CloseActiveScreen)
                    },
                    onSaved = {
                        onEvent(SchoolsUiEvent.RefreshRequested)
                        onEvent(SchoolsUiEvent.CloseActiveScreen)
                    }
                )
            } else if (showDetails != null) {
                SchoolDetailsFeatureRoute(
                    school = showDetails,
                    onBack = { onEvent(SchoolsUiEvent.CloseDetailView) }
                )
            }

        pendingSchoolAction?.let { pending ->
            val isDelete = pending.action == SchoolPendingActionType.Delete
            val isActive = pending.school.isActive
            val actionLabel = if (isActive) "Inactivate" else "Activate"
            val title = if (isDelete) {
                "Delete School"
            } else {
                "$actionLabel School"
            }
            val subtitle = if (isDelete) {
                "This will move the school record to inactive records."
            } else {
                "This will update the school status without removing the record."
            }
            val bodyText = if (isDelete) {
                "Delete ${pending.school.name}?"
            } else {
                "$actionLabel ${pending.school.name}?"
            }
            AppConfirmationDialog(
                onDismiss = { pendingSchoolAction = null },
                title = title,
                subtitle = subtitle,
                bodyText = bodyText,
                confirmLabel = if (isDelete) "Delete" else actionLabel,
                tone = if (isDelete) AppConfirmationDialogTone.Danger else AppConfirmationDialogTone.Primary,
                onConfirm = {
                    pendingSchoolAction = null
                    when (pending.action) {
                        SchoolPendingActionType.Delete -> onEvent(SchoolsUiEvent.DeleteSchool(pending.school.id))
                        SchoolPendingActionType.ToggleStatus -> onEvent(SchoolsUiEvent.ToggleSchoolStatus(pending.school.id))
                    }
                }
            )
        }

        if (showFilterSheet) {
            SchoolsFilterModal(
                draft = filterDraft,
                onDraftChange = { filterDraft = it },
                onApply = {
                    onEvent(SchoolsUiEvent.FilterChanged(filterDraft.copy(location = "")))
                    showFilterSheet = false
                },
                onReset = { filterDraft = SchoolFilterCriteria() },
                onCancel = { showFilterSheet = false }
            )
        }
    }
}

@Composable
private fun HeaderSection() {
    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Text(
            text = "Schools",
            style = MaterialTheme.typography.headlineMedium,
            color = AppPageHeader,
            fontWeight = FontWeight.Bold
        )
        Text(
            text = "Manage all schools and their status",
            style = MaterialTheme.typography.titleMedium,
            color = AppPageSubtitle
        )
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun StatusFilterChip(
    label: String,
    selected: Boolean,
    onClick: () -> Unit
) {
    androidx.compose.material3.Surface(
        onClick = onClick,
        shape = RoundedCornerShape(999.dp),
        color = if (selected) SchoolsBlue else SchoolsSurface,
        contentColor = if (selected) Color.White else SchoolsTextPrimary,
        border = androidx.compose.foundation.BorderStroke(1.dp, SchoolsBorder)
    ) {
        Text(
            text = label,
            modifier = Modifier.padding(horizontal = 14.dp, vertical = 8.dp),
            style = MaterialTheme.typography.labelLarge,
            fontWeight = FontWeight.SemiBold
        )
    }
}

@Composable
private fun EditorField(
    value: String,
    label: String,
    onValueChange: (String) -> Unit
) {
    AppOutlinedTextField(
        value = value,
        onValueChange = onValueChange,
        modifier = Modifier.fillMaxWidth(),
        label = label
    )
}

private enum class SchoolPendingActionType {
    Delete,
    ToggleStatus
}

private data class SchoolPendingAction(
    val school: SchoolRowUi,
    val action: SchoolPendingActionType
)

private fun schoolTableShimmerColumns(): List<AdminListHeaderColumn> = listOf(
    AdminListHeaderColumn(label = "School Name", weight = 1.8f),
    AdminListHeaderColumn(label = "Code", weight = 1.0f),
    AdminListHeaderColumn(label = "City", weight = 1.0f),
    AdminListHeaderColumn(label = "Contact Name", weight = 1.2f),
    // Keep the placeholders anchored like SchoolRowCard: Students and Status
    // start at the beginning of their cells, while the action affordance is
    // centered in its narrower column.
    AdminListHeaderColumn(label = "Students", weight = 0.8f),
    AdminListHeaderColumn(label = "Status", weight = 0.9f),
    AdminListHeaderColumn(label = "Actions", weight = 0.95f)
)

